import { addFilter } from "@wordpress/hooks";
import { createHigherOrderComponent } from "@wordpress/compose";
import {
    ToggleControl,
    RangeControl,
    PanelBody,
    __experimentalView as View,
    SelectControl,
} from "@wordpress/components";
import { InspectorControls, PanelColorSettings } from "@wordpress/block-editor";
import { Fragment, useEffect } from "@wordpress/element";
import UnitRange from "./components/UnitRange";

// Dodajemy nowe atrybuty do bloku Typography
function addCircleAttributes(settings, name) {
    if (name !== "core/heading") {
        return settings;
    }

    return {
        ...settings,
        attributes: {
            ...settings.attributes,
            circleSizeUnit: {
                type: "string",
                default: "px",
            },
            hasCircle: {
                type: "boolean",
                default: false,
            },
            circleColor: {
                type: "string",
                default: "#000000",
            },
            circleSize: {
                type: "number",
                default: 10,
            },
            circleMinSize: {
                type: "number",
                default: 8,
            },
            circleMaxSize: {
                type: "number",
                default: 16,
            },
            positionStyle: {
                type: "string",
                default: "relative",
            },
        },
    };
}

// Dodajemy kontrolki do panelu inspektora
const withCircleControls = createHigherOrderComponent((BlockEdit) => {
    return function WithCircleControls(props) {
        if (props.name !== "core/heading") {
            return <BlockEdit {...props} />;
        }

        const { attributes, setAttributes } = props;

        useEffect(() => {
            const currentClassName = attributes.className || "";
            const hasCircleClass = currentClassName.includes("has-circle");

            if (attributes.hasCircle && !hasCircleClass) {
                setAttributes({
                    className: currentClassName
                        ? `${currentClassName} has-circle`
                        : "has-circle",
                });
            } else if (!attributes.hasCircle && hasCircleClass) {
                setAttributes({
                    className: currentClassName
                        .replace("has-circle", "")
                        .trim(),
                });
            }
        }, [attributes.hasCircle]);

        // Dodajemy style bezpośrednio do bloku w edytorze
        const blockProps = {
            ...props,
            style: {
                ...props.style,
                position: attributes.positionStyle,
            },
        };

        if (attributes.hasCircle) {
            // Dynamiczny, responsywny clamp dla rozmiaru kropki
            const clampedCircleSize = `clamp(${attributes.circleMinSize}${attributes.circleSizeUnit}, ${attributes.circleSize}${attributes.circleSizeUnit} + ((1vw - 0.48rem) * 1.5), ${attributes.circleMaxSize}${attributes.circleSizeUnit})`;

            blockProps.style = {
                ...blockProps.style,
                "--circle-color": attributes.circleColor,
                "--circle-size": clampedCircleSize,
            };
        }

        return (
            <Fragment>
                <BlockEdit {...blockProps} />
                <InspectorControls>
                    <PanelBody title="Ustawienia pozycji" initialOpen={false}>
                        <View style={{ padding: "15px 0 15px 0" }}>
                            <SelectControl
                                label="Position"
                                value={attributes.positionStyle}
                                options={[
                                    { label: "Static", value: "static" },
                                    { label: "Relative", value: "relative" },
                                    { label: "Absolute", value: "absolute" },
                                    { label: "Fixed", value: "fixed" },
                                    { label: "Sticky", value: "sticky" },
                                ]}
                                onChange={(value) => {
                                    setAttributes({ positionStyle: value });
                                }}
                            />
                        </View>
                    </PanelBody>
                    <PanelBody title="Dot settings" initialOpen={false}>
                        <View style={{ padding: "25px 0 15px 0" }}>
                            <ToggleControl
                                label="Dodaj kropkę przed nagłówkiem"
                                checked={attributes.hasCircle}
                                onChange={(value) => {
                                    setAttributes({ hasCircle: value });
                                }}
                                __nextHasNoMarginBottom={true}
                            />
                        </View>
                        {attributes.hasCircle && (
                            <>
                                <View style={{ padding: "15px 0 15px 0" }}>
                                    <UnitRange
                                        label="Minimalna wielkość"
                                        rangeValue={attributes.circleMinSize}
                                        onRangeChange={(value) =>
                                            setAttributes({
                                                circleMinSize: value,
                                            })
                                        }
                                        unitValue={{
                                            value: attributes.circleMinSize,
                                            unit: attributes.circleSizeUnit,
                                        }}
                                        onUnitChange={(newValue) =>
                                            setAttributes({
                                                circleMinSize: newValue.value,
                                                circleSizeUnit: newValue.unit,
                                            })
                                        }
                                        min={0}
                                        max={20}
                                        hasNoMarginBottom
                                        units={["px", "em", "rem", "%"]}
                                    />
                                </View>
                                <View style={{ padding: "15px 0 15px 0" }}>
                                    <UnitRange
                                        label="Maksymalna wielkość"
                                        rangeValue={attributes.circleMaxSize}
                                        onRangeChange={(value) =>
                                            setAttributes({
                                                circleMaxSize: value,
                                            })
                                        }
                                        unitValue={{
                                            value: attributes.circleMaxSize,
                                            unit: attributes.circleSizeUnit,
                                        }}
                                        onUnitChange={(newValue) =>
                                            setAttributes({
                                                circleMaxSize: newValue.value,
                                                circleSizeUnit: newValue.unit,
                                            })
                                        }
                                        min={attributes.circleMinSize}
                                        max={30}
                                        hasNoMarginBottom
                                        units={["px", "em", "rem", "%"]}
                                    />
                                </View>
                                <View style={{ padding: "0 0 15px 0" }}>
                                    <PanelColorSettings
                                        title="Wybierz kolor"
                                        initialOpen={true}
                                        enableAlpha={true}
                                        colorSettings={[
                                            {
                                                value: attributes.circleColor,
                                                onChange: (color) =>
                                                    setAttributes({
                                                        circleColor: color,
                                                    }),
                                                label: "Kolor kropki",
                                            },
                                        ]}
                                    />
                                </View>
                            </>
                        )}
                    </PanelBody>
                </InspectorControls>
            </Fragment>
        );
    };
}, "withCircleControls");

// Dodajemy style do paragrafu
const addCircleStyles = (extraProps, blockType, attributes) => {
    if (blockType.name !== "core/heading") {
        return extraProps;
    }

    const styles = {
        ...extraProps.style,
        position: attributes.positionStyle,
    };

    if (attributes.hasCircle) {
        extraProps.className = extraProps.className
            ? `${extraProps.className} has-circle`
            : "has-circle";

        // Dynamiczny, responsywny clamp dla rozmiaru kropki
        const clampedCircleSize = `clamp(${attributes.circleMinSize}${attributes.circleSizeUnit}, ${attributes.circleSize}${attributes.circleSizeUnit} + ((1vw - 0.48rem) * 3), ${attributes.circleMaxSize}${attributes.circleSizeUnit})`;

        styles["--circle-color"] = attributes.circleColor;
        styles["--circle-size"] = clampedCircleSize;
    }

    extraProps.style = styles;
    return extraProps;
};

// Rejestrujemy filtry
addFilter(
    "blocks.registerBlockType",
    "custom/circle/add-attributes",
    addCircleAttributes
);
addFilter("editor.BlockEdit", "custom/circle/add-control", withCircleControls);
addFilter(
    "blocks.getSaveContent.extraProps",
    "custom/circle/add-styles",
    addCircleStyles
);
